
import { MeetingNote } from "../types";

/**
 * Decodes a base64url encoded string to a UTF-8 string.
 */
function decodeBase64(b64: string): string {
  try {
    const base64 = b64.replace(/-/g, '+').replace(/_/g, '/');
    return decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
  } catch (e) {
    console.error("Failed to decode base64 string", e);
    return "";
  }
}

/**
 * Recursively extracts the plain text body from the Gmail message payload.
 */
function getMessageBody(payload: any): string {
  let body = "";

  if (payload.mimeType === 'text/plain' && payload.body && payload.body.data) {
    body = decodeBase64(payload.body.data);
  } else if (payload.parts) {
    // Look for text/plain parts first
    const plainTextPart = payload.parts.find((part: any) => part.mimeType === 'text/plain');
    if (plainTextPart && plainTextPart.body && plainTextPart.body.data) {
      body = decodeBase64(plainTextPart.body.data);
    } else {
      // If no direct plain text part, recurse through all parts (e.g., multipart/alternative)
      for (const part of payload.parts) {
        const nestedBody = getMessageBody(part);
        if (nestedBody) {
          body = nestedBody;
          break; 
        }
      }
    }
  }
  
  return body;
}

export async function fetchGmailMeetingNotes(accessToken: string, days: number = 14): Promise<MeetingNote[]> {
  /**
   * Dynamic query:
   * - Searches for "Meeting Transcript", "Meeting notes", or "Meeting summary" in the subject
   * - Or any email mentioning "Gemini"
   * - Lookback window is controlled by the 'days' parameter
   */
  const query = `(subject:("Meeting Transcript" OR "Meeting notes" OR "Meeting summary") OR "Gemini") newer_than:${days}d`;
  const searchUrl = `https://gmail.googleapis.com/gmail/v1/users/me/messages?q=${encodeURIComponent(query)}`;

  const response = await fetch(searchUrl, {
    headers: { Authorization: `Bearer ${accessToken}` }
  });

  if (!response.ok) {
    let errorDetail = "";
    try {
      const errorJson = await response.json();
      errorDetail = errorJson.error?.message || JSON.stringify(errorJson);
    } catch (e) {
      errorDetail = await response.text();
    }
    
    if (response.status === 403) {
      throw new Error(`Gmail API Access Denied (403): ${errorDetail}. Please ensure the 'Gmail API' is enabled in your Google Cloud Console.`);
    } else if (response.status === 401) {
      throw new Error(`Unauthorized (401): Your session may have expired. Please try re-connecting.`);
    }
    
    throw new Error(`Failed to search Gmail (${response.status}): ${errorDetail}`);
  }

  const data = await response.json();
  if (!data.messages) return [];

  // Limit to 15 for performance and token context limits
  const messagePromises = data.messages.slice(0, 15).map(async (msg: any) => {
    try {
      const msgRes = await fetch(`https://gmail.googleapis.com/gmail/v1/users/me/messages/${msg.id}`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      
      if (!msgRes.ok) return null;
      
      const msgData = await msgRes.json();
      const headers = msgData.payload.headers;
      
      const subject = headers.find((h: any) => h.name === "Subject")?.value || "Untitled Meeting";
      const dateHeader = headers.find((h: any) => h.name === "Date")?.value;
      const date = dateHeader ? new Date(dateHeader).toISOString().split('T')[0] : "Recent";
      
      // Extract full body content instead of just the snippet
      const fullBody = getMessageBody(msgData.payload);
      const content = fullBody || msgData.snippet || "No content available.";

      return {
        id: msg.id,
        title: subject.replace(/Meeting Transcript: |Meeting Notes: /g, ""),
        date,
        content: content.slice(0, 15000), // Safety cap for extremely long transcripts
        source: 'gmail' as const,
        url: `https://mail.google.com/mail/u/0/#all/${msg.id}`
      };
    } catch (e) {
      console.warn(`Failed to fetch message details for ${msg.id}`, e);
      return null;
    }
  });

  const results = await Promise.all(messagePromises);
  return results.filter((n): n is MeetingNote => n !== null);
}

/**
 * Sends a summary email via Gmail API.
 */
function markdownToHtml(text: string): string {
  if (!text) return '';
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/\n/g, '<br/>');
}

export async function sendEmailNotification(
  accessToken: string, 
  recipientEmail: string, 
  summary: any
): Promise<void> {
  const subject = `MeetingBot: Weekly Intelligence Report - ${new Date().toLocaleDateString()}`;
  
  // Construct a clean HTML body
  let body = `
    <div style="font-family: sans-serif; max-width: 600px; margin: 0 auto; color: #334155;">
      <h1 style="color: #4f46e5; border-bottom: 2px solid #e2e8f0; padding-bottom: 10px;">Weekly Intelligence Report</h1>
      <p style="font-size: 16px; line-height: 1.6;">${markdownToHtml(summary.conciseOverview)}</p>
      
      <h2 style="color: #059669; margin-top: 30px;">Key Outcomes</h2>
      <ul style="padding-left: 20px;">
        ${summary.keyOutcomes.map((outcome: string) => `<li style="margin-bottom: 8px;">${markdownToHtml(outcome)}</li>`).join('')}
      </ul>
      
      <h2 style="color: #d97706; margin-top: 30px;">Ownership & Tasks</h2>
      ${summary.tasksPerPerson.map((person: any) => `
        <div style="background: #f8fafc; padding: 15px; border-radius: 8px; margin-bottom: 15px; border: 1px solid #e2e8f0;">
          <h3 style="margin-top: 0; color: #1e293b;">${person.name}</h3>
          <ul style="padding-left: 20px; margin-bottom: 0;">
            ${person.tasks.map((task: string) => `<li style="margin-bottom: 5px;">${markdownToHtml(task)}</li>`).join('')}
          </ul>
        </div>
      `).join('')}
      
      <h2 style="color: #6366f1; margin-top: 30px;">Top Themes</h2>
      <div style="display: flex; flex-wrap: wrap; gap: 10px;">
        ${summary.topThemes.map((theme: string) => `
          <span style="background: #eef2ff; color: #4338ca; padding: 5px 12px; border-radius: 20px; font-size: 12px; font-weight: bold; border: 1px solid #c7d2fe; margin-right: 5px; margin-bottom: 5px; display: inline-block;">
            ${theme.toUpperCase()}
          </span>
        `).join('')}
      </div>
      
      <hr style="margin-top: 40px; border: 0; border-top: 1px solid #e2e8f0;" />
      <p style="font-size: 10px; color: #94a3b8; text-align: center; text-transform: uppercase; letter-spacing: 1px;">
        Generated by MeetingBot • Gemini Intelligence Engine
      </p>
    </div>
  `;

  // Encode subject for non-ASCII characters
  const utf8Subject = `=?utf-8?B?${btoa(unescape(encodeURIComponent(subject)))}?=`;
  
  const messageParts = [
    `To: ${recipientEmail}`,
    `Subject: ${utf8Subject}`,
    'MIME-Version: 1.0',
    'Content-Type: text/html; charset=utf-8',
    '',
    body
  ];
  const message = messageParts.join('\n');

  // Base64url encode the entire message
  const encodedMessage = btoa(unescape(encodeURIComponent(message)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

  const response = await fetch('https://gmail.googleapis.com/gmail/v1/users/me/messages/send', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ raw: encodedMessage })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(`Failed to send email: ${error.error?.message || response.statusText}`);
  }
}
